/**
 * Pure ICS (iCalendar) string builder — no browser APIs. The presentation
 * layer is responsible for turning the returned string into a downloadable
 * blob/link.
 */
export interface IcsEventInput {
  uid: string;
  title: string;
  description?: string;
  location?: string;
  /** ISO 8601 datetime string. */
  startDatetime: string;
  /** ISO 8601 datetime string. */
  endDatetime: string;
}

function toIcsDate(iso: string): string {
  const date = new Date(iso);
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function buildIcsEvent(input: IcsEventInput): string {
  const now = toIcsDate(new Date().toISOString());

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ItsHealer//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${input.uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${toIcsDate(input.startDatetime)}`,
    `DTEND:${toIcsDate(input.endDatetime)}`,
    `SUMMARY:${escapeIcsText(input.title)}`,
  ];

  if (input.description) {
    lines.push(`DESCRIPTION:${escapeIcsText(input.description)}`);
  }

  if (input.location) {
    lines.push(`LOCATION:${escapeIcsText(input.location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.join("\r\n");
}

/** Builds a Google Calendar "add event" deep-link for the given event. */
export function buildGoogleCalendarUrl(input: IcsEventInput): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: input.title,
    dates: `${toIcsDate(input.startDatetime)}/${toIcsDate(input.endDatetime)}`,
  });

  if (input.description) {
    params.set("details", input.description);
  }

  if (input.location) {
    params.set("location", input.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
